import { useState } from 'react';
import { useAuth, DEV_USERS, isGlobalAdmin } from '../hooks/useAuth';

const ROLE_CATEGORIES = [
  {
    name: 'Global Roles',
    roles: [
      { key: 'superAdmin', label: 'Super Admin', color: 'bg-purple-500', description: 'Full access to everything' },
      { key: 'admin', label: 'Global Admin', color: 'bg-indigo-500', description: 'Full access, cannot promote to superadmin' },
    ],
  },
  {
    name: 'Project A Users',
    roles: [
      { key: 'projectAAdmin', label: 'Project A Admin', color: 'bg-blue-500', description: 'Admin for Project_A and all its teams' },
      { key: 'projectAEditor', label: 'Project A Editor', color: 'bg-green-500', description: 'Editor for Project_A and all its teams' },
      { key: 'projectAViewer', label: 'Project A Viewer', color: 'bg-gray-500', description: 'Viewer for Project_A and all its teams' },
    ],
  },
  {
    name: 'Project B Users',
    roles: [
      { key: 'projectBAdmin', label: 'Project B Admin', color: 'bg-blue-500', description: 'Admin for Project_B and all its teams' },
      { key: 'projectBEditor', label: 'Project B Editor', color: 'bg-green-500', description: 'Editor for Project_B and all its teams' },
      { key: 'projectBViewer', label: 'Project B Viewer', color: 'bg-gray-500', description: 'Viewer for Project_B and all its teams' },
    ],
  },
  {
    name: 'Team-Specific Users (Team A only)',
    roles: [
      { key: 'teamAAdmin', label: 'Team A Admin', color: 'bg-blue-400', description: 'Admin for Project_A_Team_A only' },
      { key: 'teamAEditor', label: 'Team A Editor', color: 'bg-green-400', description: 'Editor for Project_A_Team_A only' },
      { key: 'teamAViewer', label: 'Team A Viewer', color: 'bg-gray-400', description: 'Viewer for Project_A_Team_A only' },
    ],
  },
];

export default function DevToolbar() {
  const { user, isLocalDev, currentDevRole, setDevUserRole } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  // Only show in local dev mode
  if (!isLocalDev) {
    return null;
  }

  // Find current role info
  const allRoles = ROLE_CATEGORIES.flatMap(c => c.roles);
  const currentRoleInfo = allRoles.find(r => r.key === currentDevRole) || allRoles[0];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Expanded panel */}
      {isExpanded && (
        <div className="bg-gray-900 border-t border-gray-700 p-4 max-h-[70vh] overflow-y-auto">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <span className="text-yellow-400">DEV</span>
                Switch User Role
              </h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {ROLE_CATEGORIES.map((category) => (
              <div key={category.name} className="mb-4">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  {category.name}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {category.roles.map((role) => {
                    const roleUser = DEV_USERS[role.key];
                    const isActive = currentDevRole === role.key;

                    return (
                      <button
                        key={role.key}
                        onClick={() => {
                          setDevUserRole(role.key);
                          setIsExpanded(false);
                        }}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          isActive
                            ? 'border-yellow-400 bg-gray-800'
                            : 'border-gray-700 bg-gray-800/50 hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-2 h-2 rounded-full ${role.color}`} />
                          <span className={`text-sm font-medium ${isActive ? 'text-yellow-400' : 'text-white'}`}>
                            {role.label}
                          </span>
                          {isActive && (
                            <svg className="w-4 h-4 text-yellow-400 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{role.description}</p>
                        <p className="text-xs text-gray-400 mt-1 truncate">{roleUser?.email}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-400">
                Current user: <span className="text-gray-300">{user?.name}</span> ({user?.email})
                {user && isGlobalAdmin(user) && (
                  <span className="ml-2 text-purple-400">[{user.role.toUpperCase()}]</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed bar */}
      <div className="bg-gray-900 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 text-xs font-bold bg-yellow-400 text-gray-900 rounded">
              DEV
            </span>
            <span className="text-gray-400 text-sm">Mock Auth</span>
            <span className="text-gray-400">|</span>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
            >
              <span className={`w-2 h-2 rounded-full ${currentRoleInfo.color}`} />
              <span>{currentRoleInfo.label}</span>
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-400">
              {user?.email}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
